import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import type { UserPinPublic, PinType, PinEntityType } from "@dugads/shared";
import { useAuth } from "@/lib/auth-context";
import * as api from "@/lib/api";

const FILTER_STORAGE_KEY = "pin-filters";

function loadStoredFilters(): Set<string> {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored) as string[]);
  } catch {
    // ignore corrupt storage
  }
  return new Set();
}

function saveFilters(ids: Set<string>) {
  localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify([...ids]));
}

interface PinsState {
  pins: UserPinPublic[];
  loading: boolean;

  // Quick-access pins
  addPin: (pinType: PinType, entityType: PinEntityType, entityId: string) => Promise<void>;
  removePin: (pinId: string) => Promise<void>;
  isPinned: (entityType: PinEntityType, entityId: string) => boolean;

  // Pin filters
  activePinIds: Set<string>;
  togglePinFilter: (pinId: string) => void;
  clearPinFilters: () => void;
  activeOrgIds: string[];
  activeSchoolIds: string[];
  activeSponsorIds: string[];
}

const PinsContext = createContext<PinsState | null>(null);

export function PinsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [pins, setPins] = useState<UserPinPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePinIds, setActivePinIds] = useState<Set<string>>(loadStoredFilters);

  const fetchPins = useCallback(async () => {
    if (!user) {
      setPins([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.getPins();
      setPins(res.pins);
      // Prune stored filter IDs that no longer exist
      setActivePinIds((prev) => {
        const validIds = new Set(res.pins.map((p) => p.id));
        const pruned = new Set([...prev].filter((id) => validIds.has(id)));
        if (pruned.size !== prev.size) saveFilters(pruned);
        return pruned;
      });
    } catch {
      // Silently fail -- pins are non-critical
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  // All pins are quick-access pins (organization_focus removed)

  const togglePinFilter = useCallback((pinId: string) => {
    setActivePinIds((prev) => {
      const next = new Set(prev);
      if (next.has(pinId)) next.delete(pinId);
      else next.add(pinId);
      saveFilters(next);
      return next;
    });
  }, []);

  const clearPinFilters = useCallback(() => {
    setActivePinIds(new Set());
    saveFilters(new Set());
  }, []);

  const activeOrgIds = useMemo(
    () =>
      pins
        .filter((p) => activePinIds.has(p.id) && p.entity_type === "Organization")
        .map((p) => p.entity_id),
    [pins, activePinIds],
  );

  // Org→school cascade: when org pins are active, derive school IDs from those orgs
  const [orgSchoolIds, setOrgSchoolIds] = useState<string[]>([]);

  useEffect(() => {
    if (activeOrgIds.length === 0) {
      setOrgSchoolIds([]);
      return;
    }
    // Fetch schools for each pinned org
    Promise.all(activeOrgIds.map((orgId) => api.getSchools(orgId)))
      .then((results) => {
        const ids = results.flatMap((r) => r.schools.map((s) => s.id));
        setOrgSchoolIds([...new Set(ids)]);
      })
      .catch(() => setOrgSchoolIds([]));
  }, [activeOrgIds]);

  const directSchoolIds = useMemo(
    () =>
      pins
        .filter((p) => activePinIds.has(p.id) && p.entity_type === "School")
        .map((p) => p.entity_id),
    [pins, activePinIds],
  );

  // Merge direct school pin IDs + org-derived school IDs (deduplicated)
  const activeSchoolIds = useMemo(
    () => [...new Set([...directSchoolIds, ...orgSchoolIds])],
    [directSchoolIds, orgSchoolIds],
  );

  const activeSponsorIds = useMemo(
    () =>
      pins
        .filter((p) => activePinIds.has(p.id) && p.entity_type === "Sponsor")
        .map((p) => p.entity_id),
    [pins, activePinIds],
  );

  const addPin = useCallback(
    async (pinType: PinType, entityType: PinEntityType, entityId: string) => {
      const res = await api.createPin({
        pin_type: pinType as "organization" | "school" | "sponsor" | "team" | "event",
        entity_type: entityType as "Organization" | "School" | "Sponsor" | "Team" | "Event",
        entity_id: entityId,
      });
      setPins((prev) => {
        // Replace if already exists (idempotent)
        const filtered = prev.filter(
          (p) => !(p.pin_type === pinType && p.entity_id === entityId),
        );
        return [...filtered, res.pin];
      });
    },
    [],
  );

  const removePin = useCallback(async (pinId: string) => {
    await api.deletePin(pinId);
    setPins((prev) => prev.filter((p) => p.id !== pinId));
    // Also remove from active filters
    setActivePinIds((prev) => {
      if (!prev.has(pinId)) return prev;
      const next = new Set(prev);
      next.delete(pinId);
      saveFilters(next);
      return next;
    });
  }, []);

  const isPinned = useCallback(
    (entityType: PinEntityType, entityId: string) => {
      return pins.some(
        (p) => p.entity_type === entityType && p.entity_id === entityId,
      );
    },
    [pins],
  );

  return (
    <PinsContext.Provider
      value={{
        pins,
        loading,
        addPin,
        removePin,
        isPinned,
        activePinIds,
        togglePinFilter,
        clearPinFilters,
        activeOrgIds,
        activeSchoolIds,
        activeSponsorIds,
      }}
    >
      {children}
    </PinsContext.Provider>
  );
}

export function usePins() {
  const ctx = useContext(PinsContext);
  if (!ctx) throw new Error("usePins must be used within PinsProvider");
  return ctx;
}

export function usePinFilters() {
  const { pins, activePinIds, togglePinFilter, clearPinFilters, activeOrgIds, activeSchoolIds, activeSponsorIds } = usePins();
  return { pins, activePinIds, togglePinFilter, clearPinFilters, activeOrgIds, activeSchoolIds, activeSponsorIds };
}
